import { datadogRum } from '@datadog/browser-rum'
import { Roles } from '@omnea-digital/toolbox'
import { SessionContextValue, signOut, useSession } from 'next-auth/react'
import { useRouter } from 'next/router'
import { useEffect, useMemo, useRef } from 'react'
import { analytics } from '@omnea-app/fe-analytics/client'
import { getMultiOrgUserTrackingId } from '@omnea-app/fe-analytics/getMultiOrgUserTrackingId'
import { useMeticulous } from '@omnea-app/fe-legacy/components/layout/wrapper/useMeticulous'
import { Redirect } from '@omnea-app/fe-routing/Redirect'
import { isExplicitSupplierPath, isSupplierPath, loginPaths, publicPaths } from './allow-paths'
import { getLoginCallbackUrl } from './getLoginCallbackUrl'
import { getTargetOrg, sanitizeRedirectTo, setTargetOrg, TARGET_ORG_STORAGE_KEY, useTokenProvider } from './TokenProvider'
import { UserSessionProvider } from './UserSessionProvider'
import { useSetDatadogRumUser } from './useSetDatadogRumUser'

const ORG_CORRECTION_ATTEMPTED_KEY = 'omnea_org_correction_attempted'

const getFirstQueryValue = (value: string | string[] | undefined): string | undefined => {
  if (typeof value === 'string') {
    return value
  }
  if (Array.isArray(value)) {
    return value[0]
  }
  return undefined
}

const getAbsoluteCallbackUrl = (path: string): string => {
  if (typeof window === 'undefined') {
    return path
  }
  return new URL(path, window.location.origin).toString()
}

const safeLocalStorageRemove = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch {
    // localStorage unavailable
  }
}

const safeSessionStorageRemove = (key: string): void => {
  try {
    sessionStorage.removeItem(key)
  } catch {
    // sessionStorage unavailable
  }
}

export const Auth: React.FC<React.PropsWithChildren> = ({ children }) => {
  const router = useRouter()
  const pathname = router.pathname
  const exactPath = router.asPath
  const query = router.query ?? {}
  const organisationIdQuery = getFirstQueryValue(query.organisationId as string | string[] | undefined)
  const callbackPath = sanitizeRedirectTo(router.asPath)

  const isPublicPath = publicPaths.includes(pathname)
  const { data: session, status } = useSession({ required: !isPublicPath })
  const { switchOrg } = useTokenProvider()
  const hasValidatedOrgRef = useRef(false)

  // Identify user in Analytics every time the session updates
  useIdentifyUser(session)

  useSetDatadogRumUser({ session })
  useMeticulous({ session })

  // Validate org state and supplier invite corrections.
  useEffect(() => {
    if (status !== 'authenticated' || session.user.type !== 'fullyAuthenticated') return

    const isSupplierInvitePath = isExplicitSupplierPath(pathname) || isExplicitSupplierPath(exactPath)
    const isSupplierInviteQueryReady = !isSupplierInvitePath || router.isReady
    if (!isSupplierInviteQueryReady) {
      return
    }
    const hasRedirectCallback = callbackPath !== '/'
    const callbackUrl = getAbsoluteCallbackUrl(callbackPath)

    if (isSupplierInvitePath && organisationIdQuery) {
      setTargetOrg(organisationIdQuery)
    }

    // Global org validation on page load - localStorage is source of truth for user intent.
    if (hasValidatedOrgRef.current) return

    // Set ref BEFORE async work to prevent duplicate calls during React re-renders.
    // switchOrg does a hard refresh on success, so subsequent renders won't occur.
    // If switchOrg fails, the ref prevents retries within the same page load (user can manually refresh).
    hasValidatedOrgRef.current = true

    const targetOrg = getTargetOrg()
    const sessionOrg = session.user.orgMetadata.organisationId

    if (targetOrg && targetOrg !== sessionOrg) {
      // Mismatch - localStorage says one org, session says another
      // This can happen if a background getSession() overwrote the cookie after org switch
      try {
        const alreadyAttempted = sessionStorage.getItem(ORG_CORRECTION_ATTEMPTED_KEY)
        if (!alreadyAttempted) {
          // First attempt - try to switch to target org
          sessionStorage.setItem(ORG_CORRECTION_ATTEMPTED_KEY, 'true')
          const switchPromise =
            hasRedirectCallback && isSupplierInvitePath
              ? switchOrg(targetOrg, { redirectTo: callbackPath })
              : switchOrg(targetOrg)
          void switchPromise.catch(() => {
            // switchOrg failed before refresh - clear flag to allow retry on manual refresh
            safeSessionStorageRemove(ORG_CORRECTION_ATTEMPTED_KEY)
          })
          return
        }
        // Already tried once, still wrong - sign out and let user start fresh
        safeSessionStorageRemove(ORG_CORRECTION_ATTEMPTED_KEY)
        safeLocalStorageRemove(TARGET_ORG_STORAGE_KEY)
        if (hasRedirectCallback && isSupplierInvitePath) {
          void signOut({ callbackUrl: `/login?callbackUrl=${encodeURIComponent(callbackUrl)}` })
        } else {
          void signOut({ callbackUrl: '/login' })
        }
        return
      } catch {
        // sessionStorage unavailable - can't safely track retries, so don't attempt correction
        // (attempting without retry tracking could cause infinite refresh loops)
        // Don't overwrite localStorage either - preserve user's intent for manual retry
        datadogRum.addError(new Error('sessionStorage unavailable during org validation'))
        return
      }
    }

    // Match or no target - clear any correction flag and sync localStorage
    safeSessionStorageRemove(ORG_CORRECTION_ATTEMPTED_KEY)

    if (!targetOrg) {
      setTargetOrg(sessionOrg)
    }
  }, [status, session, router.isReady, pathname, exactPath, organisationIdQuery, callbackPath, switchOrg])

  if (isPublicPath) {
    const isLoginPath = loginPaths.includes(pathname)
    const isAuthenticated = status === 'authenticated'
    if (isLoginPath && isAuthenticated) {
      // Wait for router.query to hydrate on dynamic routes (e.g. /login/[[...id]])
      // before reading callbackUrl — otherwise getLoginCallbackUrl falls back to '/'
      if (!router.isReady) return null
      return <Redirect to={getLoginCallbackUrl(router)} type="replace" />
    }

    return <>{children}</>
  }

  // If the user is not authenticated we render a blank page
  // Only briefly, because it will be redirected to the login page by NextAuth, through the useSession hook
  if (status !== 'authenticated') {
    return null
  }

  if (session?.user.type === 'organisationPicker') {
    if (pathname !== '/choose-organisation') {
      // Wait for router.asPath to stabilise before capturing it as callbackUrl
      if (!router.isReady) return null
      return <Redirect to={{ pathname: '/choose-organisation', query: { callbackUrl: router.asPath } }} type="replace" />
    }

    return <>{children}</>
  }

  const fullyAuthenticatedSessionUser = session.user
  const isAdmin = fullyAuthenticatedSessionUser.metadata.role === Roles.ADMIN

  const isSupplier = fullyAuthenticatedSessionUser.metadata.role === Roles.SUPPLIER
  if (isSupplier && !isSupplierPath(pathname) && !isSupplierPath(exactPath)) {
    return <Redirect to={{ pathname: '/' }} />
  }

  return (
    <UserSessionProvider sessionUser={fullyAuthenticatedSessionUser} isAdmin={isAdmin}>
      {children}
    </UserSessionProvider>
  )
}

function useIdentifyUser(session: SessionContextValue['data']) {
  // useMemo instead of useEffect to run synchronously before rendering
  useMemo(() => {
    if (!session) {
      return
    }

    const { email } = session.user

    if (session.user.type === 'organisationPicker') {
      const id = getMultiOrgUserTrackingId(email)

      void analytics.then((analytics) => analytics.identify(id, { email }))

      return
    }

    const { id, firstName, lastName, role, hmac } = session.user.metadata
    const { organisationId, name, isQaOrg } = session.user.orgMetadata

    void analytics.then((analytics) =>
      analytics.identify(id, {
        email,
        role,
        firstName,
        lastName,
        organisationId,
        organisationName: name,
        isQaOrg,
        userId: id,
        user_hash: hmac?.pylonUserHash || undefined,
      })
    )
    void analytics.then((analytics) =>
      analytics.group(organisationId, {
        name,
      })
    )
  }, [session])
}
